import { Client, ClientOptions } from 'models/flexible/Client';
import { GroupType, GroupTypeOptions } from 'models/flexible/GroupType';
import type { Typed } from 'services/api/fields/core';

export interface GroupOptions {
  groupType?: GroupTypeOptions;
  client?: ClientOptions;
  networkClients?: ClientOptions[];
  healthPlanClassificationIds?: string[];
  groups?: GroupOptions[];
  owners?: GroupOptions[];
  [key: string]: any;
}

function getDefaults(): GroupOptions {
  return {
    id: '',
    activeUsersCount: undefined,
    archived: false,
    name: '',
    address: {
      streetAddress: '',
      streetAddressLine2: '',
      city: '',
      state: '',
      zip: '',
    },
    client: {} as GroupOptions['client'],
    clientId: '',
    clientName: '',
    externalId: '',
    groupType: undefined,
    groupTypeId: '',
    groups: [],
    healthPlanClassificationIds: [],
    networkClients: [],
    owners: [],
    timezone: '',
    type: '',
    subType: '',
  };
}

// eslint-disable-next-line prefer-const
let Group: new <T extends GroupOptions = GroupOptions>(options: T) => Typed<_Group<T>, T>;

class _Group<T extends GroupOptions = GroupOptions> {
  groupType!: GroupType<NonNullable<T['groupType']>>;
  client!: Client<NonNullable<T['client']>>;
  networkClients!: Client<NonNullable<T['networkClients']>[number]>[];
  healthPlanClassificationIds!: string[];
  private _groups: GroupOptions[];
  private _owners: GroupOptions[];

  constructor(options: T) {
    const opts = { ...getDefaults(), ...options };
    const { groups: _groups_, owners: _owners_, ...rest } = opts;
    Object.assign(this, rest);

    this.groupType = options.groupType
      ? new GroupType<NonNullable<T['groupType']>>(options.groupType)
      : new GroupType();
    this.client = new Client<NonNullable<T['client']>>(opts.client!);
    this.networkClients = (opts.networkClients ?? []).map(
      (c) => new Client<NonNullable<T['networkClients']>[number]>(c)
    );
    this.healthPlanClassificationIds = opts.healthPlanClassificationIds ?? [];
    this._groups = _groups_ ?? [];
    this._owners = _owners_ ?? [];
  }

  is(type: string) {
    return this.groupType?.apiName === type;
  }

  get groups() {
    return this._groups.map((g) => new Group(g));
  }

  get owners() {
    return this._owners.map((o) => new Group(o));
  }

  get isProvider() {
    return !!this.groupType?.isProvider;
  }

  get isHospital() {
    return this.groupType?.apiName === 'hospital';
  }

  get isPayer() {
    return this.groupType?.apiName === 'payer';
  }

  get isPhysicianGroup() {
    return this.groupType?.apiName === 'physician_group';
  }

  get isAffiliate() {
    return this.groupType?.apiName === 'affiliate';
  }

  get isPhysicianTeam() {
    return this.groupType?.apiName === 'physician_team';
  }

  hasHealthPlanClassification(classificationId: string | undefined | null) {
    if (!classificationId) return false;
    return this.healthPlanClassificationIds.includes(classificationId);
  }
}

Group = _Group as typeof Group;

export default Group;
export { Group };
export type Group<T extends GroupOptions = GroupOptions> = _Group<T> & Omit<T, keyof _Group<T>>;
